import { computed, inject } from 'vue'
import { getErrorMessage } from '@/utils/error.js'
import { useNotification } from '@/composables/useNotification.js'
import { useTimify } from '@/composables/useTimify.js'
import { useDeviceStore } from '@/stores/device.js'
import { useAppointmentsStore } from '@/stores/appointments.js'

export function useNotes() {
  const cl = inject('cl')
  const { showNotification } = useNotification()
  const deviceStore = useDeviceStore()
  const currentStoreReference = computed(() => deviceStore.settings.warehouse)
  const timify = useTimify()
  const appointmentsStore = useAppointmentsStore()

  async function fetchOrderNotes(id) {
    try {
      return await cl.orders.attachments(id, {
        filters: {
          reference_origin_eq: 'app-orders--note',
          reference_not_eq: 'app-orders--deleted-note'
        },
        include: ['attachable'],
        fields: {
          orders: ['number']
        },
        sort: { updated_at: 'desc' }
      })
    } catch (error) {
      showNotification({
        message: getErrorMessage(error),
        variant: 'danger'
      })
    }
  }

  async function fetchTimifyNotes(customerEmail) {
    const data = []
    if (customerEmail && appointmentsStore.currentCompanyId) {
      try {
        const users = await timify.user({
          companyId: appointmentsStore.currentCompanyId,
          email: customerEmail
        })
        // i don't know how many we have
        // probably just one now
        // there are a lot of users of me but this was from a while ago
        // still potentially a lot
        // so don't request in parallel
        // it either doesn't matter or prevents rate limiting
        for (const user of users?.data || []) {
          try {
            const appointments = await timify.appointments(appointmentsStore.currentCompanyId, { customerId: user.id })
            const notes = (appointments?.data || [])
              .reduce((acc, appointment) => {
                const description = appointment?.customfields?.find(field => {
                  return field.externalId === 'customer_note'
                })?.value

                if (description) {
                  acc = {
                    ...acc,
                    [appointment.createdAt]: {
                      description,
                      customer_note: true,
                      created_at: appointment.createdAt,
                      updated_at: appointment.updatedAt,
                    }
                  }
                }

                return acc
              }, {})
            data.push(...Object.values(notes))
          } catch {
            // ignore
          }
        }
      } catch {
        // ignore
      }
    }

    return data
  }

  async function fetchCustomerNotes(id, { errorNotify = true, errorCatch = true, timifyNotes = true } = {}) {
    try {
      const response = await cl.customers.attachments(id, {
        filters: {
          reference_origin_eq: 'app-orders--note',
          reference_not_eq: 'app-orders--deleted-note'
        },
        include: ['attachable'],
        fields: {
          customers: ['email']
        },
        sort: { updated_at: 'desc' }
      })
      if (timifyNotes) {
        const [first] = response
        let customerEmail = first?.attachable?.email
        if (!customerEmail) {
          const customer = await cl.customers.retrieve(id, {
            fields: {
              customers: ['email']
            }
          })
          customerEmail = customer.email
        }
        const timifyResponse = await fetchTimifyNotes(customerEmail)
        response.push(...timifyResponse)
      }
      return response.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at)
      })
    } catch (error) {
      if (errorNotify) {
        showNotification({
          message: getErrorMessage(error),
          variant: 'danger'
        })
      }
      if (!errorCatch) {
        throw error
      }
    }
  }

  async function createOrderNote(id, note, { metadata = {} } = {}) {
    if (note === '') {
      return
    }
    try {
      await cl.attachments.create({
        name: deviceStore.settings.currentEmployee.name,
        description: note,
        reference: currentStoreReference.value,
        reference_origin: 'app-orders--note',
        metadata: {
          employee_created: {
            eid: deviceStore.settings.currentEmployee.eid,
            name: deviceStore.settings.currentEmployee.name
          },
          ...metadata
        },
        attachable: cl.orders.relationship(id)
      })
    } catch (error) {
      showNotification({
        message: getErrorMessage(error),
        variant: 'danger'
      })
    }
  }

  async function createCustomerNote(id, note, { errorNotify = true, errorCatch = true, metadata = {} } = {}) {
    if (note === '') {
      return
    }
    try {
      await cl.attachments.create({
        name: deviceStore.settings.currentEmployee.name,
        description: note,
        reference: currentStoreReference.value,
        reference_origin: 'app-orders--note',
        metadata: {
          employee_created: {
            eid: deviceStore.settings.currentEmployee.eid,
            name: deviceStore.settings.currentEmployee.name
          },
          ...metadata
        },
        attachable: cl.customers.relationship(id)
      })
    } catch (error) {
      if (errorNotify) {
        showNotification({
          message: getErrorMessage(error),
          variant: 'danger'
        })
      }
      if (!errorCatch) {
        throw error
      }
    }
  }

  return {
    fetchCustomerNotes,
    fetchOrderNotes,
    createOrderNote,
    createCustomerNote
  }
}
